import { Card, CardContent, Box, Typography, Stack, Avatar, Chip } from "@mui/material";
import {
  RadioButtonUnchecked as OpenIcon,
  LocalShipping as DispatchedIcon,
  LocationOn as OnSiteIcon,
  CheckCircle as CompletedIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import type { WorkOrderStats } from "../types";

interface WorkOrderStatsCardsProps {
  stats: WorkOrderStats;
}

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: "error" | "warning" | "success" | "info";
}

const StatCard = ({ title, value, icon, color }: StatCardProps) => (
  <Card variant="outlined">
    <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="caption" color="text.secondary">
            {title}
          </Typography>
          <Typography variant="h5">{value}</Typography>
        </Box>
        <Avatar sx={{ bgcolor: `${color}.main`, color: `${color}.contrastText`, width: 36, height: 36 }}>
          {icon}
        </Avatar>
      </Stack>
    </CardContent>
  </Card>
);

export const WorkOrderStatsCards = ({ stats }: WorkOrderStatsCardsProps) => {
  return (
    <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", gap: 2 }}>
      {/* Priority breakdown */}
      <Card variant="outlined" sx={{ minWidth: 240 }}>
        <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
            By Priority
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            <Chip label={`${stats.byPriority.critical} Critical`} size="small" color="error" />
            <Chip label={`${stats.byPriority.high} High`} size="small" color="warning" />
            <Chip label={`${stats.byPriority.medium} Medium`} size="small" color="info" variant="outlined" />
            <Chip label={`${stats.byPriority.low} Low`} size="small" color="success" variant="outlined" />
          </Stack>
        </CardContent>
      </Card>

      {/* SLA Breached */}
      {stats.slaBreached > 0 && (
        <Card variant="outlined" sx={{ minWidth: 140, bgcolor: "error.50" }}>
          <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: "error.main" }}>
                <WarningIcon fontSize="small" />
              </Avatar>
              <Box>
                <Typography variant="caption" color="error.main">SLA Breached</Typography>
                <Typography variant="h6" color="error.main">{stats.slaBreached}</Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Status cards */}
      <Box sx={{ display: "flex", gap: 2, flex: 1, minWidth: 0 }}>
        <Box sx={{ flex: 1, minWidth: 100 }}>
          <StatCard
            title="Open"
            value={stats.open}
            icon={<OpenIcon fontSize="small" />}
            color="error"
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 100 }}>
          <StatCard
            title="Dispatched"
            value={stats.dispatched + stats.techAware}
            icon={<DispatchedIcon fontSize="small" />}
            color="warning"
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 100 }}>
          <StatCard
            title="On Site"
            value={stats.onSite}
            icon={<OnSiteIcon fontSize="small" />}
            color="info"
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 100 }}>
          <StatCard
            title="Completed"
            value={stats.completed + stats.closed}
            icon={<CompletedIcon fontSize="small" />}
            color="success"
          />
        </Box>
      </Box>
    </Stack>
  );
};
